import { FC, useEffect } from "react";
import {
  Box,
  Typography,
  Divider,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { Colors } from "../../../configs";
import { CustomButton, CustomTextBox } from "../../atoms";
import { CustomDrawer } from "../../atoms/custom-drawer/custom-drawer";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";
import {
  createRoleAction,
  getAllRoles,
  getAllUsers,
  setUserRoleAction,
  updatePermissionsAction,
  updateRoleAction,
} from "../../../redux/actions/authAction";

const schema = Yup.object().shape({
  role: Yup.string().required("Role name is required"),
  permissions: Yup.array().of(Yup.string()).default([]),
});

type FormData = Yup.InferType<typeof schema>;

export interface IAddUserRoleDrawerProps {
  open: boolean;
  toggleDrawer: () => void;
  populateFor?: {
    id: number;
    name: string;
    description: string;
    permissions: { id: number; name: string; description: string }[];
  };
}

const ROLES = [
  { id: 1, name: "ADMIN", description: "Admin" },
  { id: 2, name: "CASHIER1", description: "Cashier 1" },
  { id: 3, name: "MERCHANT_ADMIN", description: "Merchant Admin" },
];

const PERMISSIONS = [
  { id: 1, name: "DROP_OFF", label: "Drop Off" },
  { id: 2, name: "PICK_UP", label: "Pick Up" },
  { id: 3, name: "EDIT_INVOICE", label: "Edit Invoice" },
  { id: 4, name: "REPRINT_INVOICE", label: "Reprint Invoice" },
  { id: 5, name: "REPRINT_TAG", label: "Reprint Tag" },
  { id: 6, name: "VOID_INVOICE", label: "Void Invoice" },
  { id: 7, name: "CONVEYOR_INPUT", label: "Conveyor Input" },
  { id: 8, name: "CASH_REGISTER", label: "Cash Register" },
  { id: 9, name: "REPORTS", label: "Reports" },
  { id: 10, name: "SEARCH", label: "Search" },
  { id: 11, name: "RETAIL_SALE", label: "Retail Sale" },
  { id: 12, name: "PRICE_LIST", label: "Price List" },
  { id: 14, name: "DAILY_SNAPSHOT", label: "Daily Snapshot" },
  { id: 15, name: "SETTINGS", label: "Settings" },
  { id: 13, name: "PICKUP_AND_DELIVERY", label: "Pickup & Delivery" },
  { id: 16, name: "MANAGER", label: "Manager" },
  { id: 17, name: "CASHIER", label: "Cashier" },
  { id: 18, name: "ADMIN", label: "Admin" },
  { id: 19, name: "MERCHANT_ADMIN", label: "Merchant Admin" },
];

export const AddUserRoleDrawer: FC<IAddUserRoleDrawerProps> = ({
  open,
  toggleDrawer,
  populateFor,
}) => {
  const dispatch = useAppDispatch();
  const snackbar = useSnackbarContext();
  const {
    setRoleData,
    updatePermissionsData,
    updatePermissionsLoading,
    setRoleLoading,
    createRoleLoading,
  } = useAppSelector((state) => state.auth);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      role: "",
      permissions: [],
    },
  });

  const selectedPermissions = watch("permissions");

  const generateDescription = (name: string) => {
    return name
      .toLowerCase()
      .split(/[_-]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  useEffect(() => {
    if (open) {
      reset({
        role: populateFor?.name ?? "",
        permissions: populateFor?.permissions?.map((p) => p.name) ?? [],
      });
    }
  }, [open, populateFor, reset]);

  const handlePermissionToggle = (permName: string) => {
    const current = selectedPermissions || [];
    if (current.includes(permName)) {
      setValue(
        "permissions",
        current.filter((p) => p !== permName)
      );
    } else {
      setValue("permissions", [...current, permName]);
    }
  };

  const handleSaveRole = async (data: FormData) => {
    try {
      if (populateFor?.id) {
        // Edit existing role
        await dispatch(
          updateRoleAction({
            roleId: populateFor.id,
            name: data.role,
            description: generateDescription(data.role),
          })
        ).unwrap();

        await dispatch(
          updatePermissionsAction({
            roleId: populateFor.id,
            permissions: (data.permissions ?? []).filter((p): p is string => p !== undefined),
          })
        ).unwrap();

        await dispatch(getAllRoles());
        snackbar.showSnackbar("Role and permissions updated!", "success");
      } else {
        // Create new role
        const result = await dispatch(
          createRoleAction({
            name: data.role,
            description: generateDescription(data.role),
          })
        ).unwrap();

        const newRoleId = result?.body?.id;
        if (newRoleId && data.permissions && data.permissions.length > 0) {
          await dispatch(
            updatePermissionsAction({
              roleId: newRoleId,
              permissions: data.permissions.filter((p): p is string => p !== undefined),
            })
          ).unwrap();
        }

        await dispatch(getAllRoles());
        snackbar.showSnackbar("Role created successfully!", "success");
      }

      toggleDrawer();
    } catch (err) {
      snackbar.showSnackbar(
        populateFor?.id ? "Failed to update. Try again." : "Failed to create role. Try again.",
        "error"
      );
    }
  };

  const isEdit = !!populateFor?.id;

  console.log(populateFor);

  return (
    <CustomDrawer
      isOpen={open}
      toggleDrawer={toggleDrawer}
      sx={{ width: "28.65rem" }}
    >
      <form onSubmit={handleSubmit(handleSaveRole)}>
        <Box
          sx={{
            paddingInline: "1.857rem",
            paddingTop: "2rem",
            display: "flex",
            flexDirection: "column",
            gap: "2.5rem",
          }}
        >
          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: "1.4rem",
              lineHeight: "1.7rem",
              color: Colors.TEXT_GRAY_DARK,
            }}
          >
            {populateFor ? "Edit Role" : "Add a New Role"}
          </Typography>

          <Box
            sx={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}
          >
            <Typography>Role Name</Typography>
            <Controller
              name="role"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <CustomTextBox
                    {...field}
                    placeholder="Enter role name"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                    sx={{ backgroundColor: Colors.WHITE }}
                  />
                </>
              )}
            />
          </Box>

          <Divider sx={{ width: "100%" }} />

          <Typography
            sx={{
              fontSize: "1.2rem",
              fontWeight: "bold",
              marginBottom: "-1.2rem",
            }}
          >
            Permissions
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              mt: 1,
            }}
          >
            {PERMISSIONS.map((perm) => (
              <Box
                key={perm.id}
                sx={{
                  flexBasis:
                    perm.name === "PICKUP_AND_DELIVERY" ? "100%" : "50%",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        selectedPermissions?.includes(perm.name) || false
                      }
                      onChange={() => handlePermissionToggle(perm.name)}
                      sx={{
                        marginLeft: "0.2rem",
                        "& .MuiSvgIcon-root": { fontSize: "1.6rem" },
                      }}
                    />
                  }
                  label={perm.label}
                  sx={{ marginTop: "-0.8rem", mb: 2 }}
                />
              </Box>
            ))}
          </Box>

          <CustomButton
            sx={{ width: "9.2rem", height: "2.6rem", marginTop: "1rem" }}
            type="submit"
            disabled={updatePermissionsLoading || setRoleLoading || createRoleLoading}
          >
            {updatePermissionsLoading || setRoleLoading || createRoleLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : populateFor ? (
              "Update Role"
            ) : (
              "Add Role"
            )}
          </CustomButton>
        </Box>
      </form>
    </CustomDrawer>
  );
};
